"use client";
import React, { useEffect, useState, useMemo } from "react";
import { CalendarDays, ChevronDown, MapPin } from "lucide-react";
import { SessionTypeBadge } from "@/components/session-type-badge";
import { sessionSlug } from "@/util/talks";
import { RECENT_SESSION_OCCURRENCES_SORTED_BY_DATE_ASC } from "@/data/derived";
import { isEventUpcoming, isEventUpcomingUtc } from "@/util/date";
import {
  formatDateMonthDay,
  formatDateMonthShort,
  formatDateDay,
} from "@/util/formatters";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "@/components/ui/external-link";

// The home page lists only the next few events to stay compact in busy
// conference seasons; the rest expand in place, the same pattern the
// Notable Feedback section uses. The heading count stays the real total,
// so nothing hides silently.
const MAX_UPCOMING_ITEMS = 6;

type UpcomingOccurrence =
  (typeof RECENT_SESSION_OCCURRENCES_SORTED_BY_DATE_ASC)[number];

function getUpcomingEvents(now: number, useUtc: boolean) {
  return RECENT_SESSION_OCCURRENCES_SORTED_BY_DATE_ASC.filter((occurrence) =>
    useUtc
      ? isEventUpcomingUtc(occurrence.date, occurrence.endDate, now)
      : isEventUpcoming(occurrence.date, occurrence.endDate, now),
  );
}

function UpcomingEventItem({
  occurrence,
  className,
}: {
  occurrence: UpcomingOccurrence;
  className: string;
}) {
  return (
    <div data-testid="upcoming-event" className={className}>
      <div className="flex items-start gap-3">
        <div className="border-border/60 bg-muted/50 rounded-lg border px-2.5 py-1.5 text-center leading-none">
          <span className="sr-only">
            {formatDateMonthDay(occurrence.startDateTimestamp)}
          </span>
          <div aria-hidden="true">
            <div
              className="text-muted-foreground text-base font-semibold tracking-wide uppercase"
              data-testid="upcoming-event-month"
            >
              {formatDateMonthShort(occurrence.startDateTimestamp)}
            </div>
            <div
              className="text-foreground text-base font-semibold"
              data-testid="upcoming-event-day"
            >
              {formatDateDay(occurrence.startDateTimestamp)}
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className="font-medium">
            {occurrence.eventWebsite ? (
              <ExternalLink
                href={occurrence.eventWebsite}
                data-testid="upcoming-event-name"
                aria-label={occurrence.event}
                className="decoration-muted-foreground/40 hover:decoration-foreground/70 inline-block min-h-6 py-0.5 underline decoration-1 underline-offset-2"
              >
                {occurrence.event}
              </ExternalLink>
            ) : (
              <span data-testid="upcoming-event-name">{occurrence.event}</span>
            )}
          </div>
          <div className="text-muted-foreground flex items-center gap-2 text-base">
            <MapPin className="size-4" aria-hidden="true" />
            {occurrence.location ?? "Online"}
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-col items-start text-base">
        {occurrence.sessions.map((session) => (
          <Link
            key={session.id}
            href={`/session/${sessionSlug(session.title)}`}
            className="group focus-visible:ring-ring/70 mt-1.5 inline-flex flex-col items-start gap-1 pb-1.5 no-underline first:mt-0 last:pb-0 hover:no-underline focus-visible:no-underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:outline-hidden sm:flex-row sm:gap-2"
          >
            <SessionTypeBadge
              type={session.type}
              className="mt-px shrink-0"
              variant="secondary"
            />
            <span className="min-w-0 pt-px leading-snug">
              <span className="decoration-muted-foreground/40 group-hover:decoration-foreground/70 wrap-break-word underline decoration-1 underline-offset-2">
                {session.title}
              </span>
              {session.slotLabel && (
                <span className="text-muted-foreground mt-0.5 block text-sm/snug">
                  {session.slotLabel}
                </span>
              )}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Upcoming({ initialNow }: { initialNow: number }) {
  const [clientNow, setClientNow] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    // Hydration complete: switch to client time.
    // Use setTimeout to avoid 'react-hooks/set-state-in-effect' warning about synchronous state updates.
    // "Warning: Calling setState synchronously within an effect can trigger cascading renders"
    const timer = setTimeout(() => setClientNow(Date.now()), 0);
    return () => clearTimeout(timer);
  }, []);

  const upcomingEvents = useMemo(() => {
    if (clientNow !== null) {
      // Client-side: use current local time and DST-safe logic
      return getUpcomingEvents(clientNow, false);
    }
    // Server-side / Hydration: use build time (UTC logic) to match server HTML
    return getUpcomingEvents(initialNow, true);
  }, [clientNow, initialNow]);

  const featuredEvents = upcomingEvents.slice(0, MAX_UPCOMING_ITEMS);
  const remainingEvents = upcomingEvents.slice(MAX_UPCOMING_ITEMS);

  if (!featuredEvents.length) return null;
  return (
    <section
      data-section-spy
      id="upcoming"
      aria-labelledby="upcoming-heading"
      data-testid="upcoming-section"
      data-page-gutter
      className="container mx-auto mt-12 scroll-mt-24 sm:mt-16"
    >
      <h2
        id="upcoming-heading"
        className="flex items-center gap-2 text-xl font-semibold tracking-tight sm:text-2xl"
      >
        <CalendarDays className="text-brand size-5" aria-hidden="true" />{" "}
        Upcoming events
        <span className="text-muted-foreground text-base font-normal">
          ({upcomingEvents.length})
        </span>
      </h2>
      <div
        className="divide-border/60 mt-6 divide-y"
        data-testid="upcoming-event-list"
      >
        {featuredEvents.map((occurrence) => (
          <UpcomingEventItem
            key={`${occurrence.event}__${occurrence.date}`}
            occurrence={occurrence}
            className="py-4 first:pt-0 last:pb-0"
          />
        ))}
      </div>

      {remainingEvents.length > 0 && (
        <>
          <div
            id="remaining-upcoming-events"
            data-testid="upcoming-expanded-container"
            // Animating grid-template-rows from 0fr to 1fr reaches the
            // content's real height, so the expanded list can never be
            // clipped by a hardcoded cap. Same pattern as Notable Feedback.
            // The collapsed content remains rendered for the height animation,
            // so inert keeps its links out of the tab order and accessibility tree.
            inert={!showAll}
            className={`grid transition-all duration-300 ease-out motion-reduce:transition-none ${showAll ? "mt-4 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"} `}
          >
            <div className="overflow-hidden">
              <div className="border-border/60 divide-border/60 divide-y border-t">
                {remainingEvents.map((occurrence) => (
                  <UpcomingEventItem
                    key={`${occurrence.event}__${occurrence.date}`}
                    occurrence={occurrence}
                    className="py-4 last:pb-0"
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => setShowAll(!showAll)}
              data-testid="upcoming-show-more-button"
              className="rounded-full"
              aria-expanded={showAll}
              aria-controls="remaining-upcoming-events"
            >
              <span>
                {showAll ? "Show less" : `Show ${remainingEvents.length} more`}
              </span>
              <ChevronDown
                className={`ml-2 size-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
